import { ReactNode } from "react";
import { SlideAction, ViewMode } from "./enums";

export interface ColumnData {
  title: string;
  /**
   * Optional pre-rendered title that bypasses i18n translation. Use this when
   * the title is dynamic content (e.g. an asset name) rather than a translation
   * key. When provided, takes precedence over `title`.
   */
  rawTitle?: string;
  mainComponent: ReactNode;
  headerComponent?: ReactNode;
  showHeader?: boolean;
  showBackButton?: boolean;
  backButtonTransition?: SlideAction;
  onBackButtonPress?: () => void;
  /**
   * Optional async guard called before the back button transition animates.
   * Return `true` to allow the transition, `false` to block it.
   * When defined, `onBackButtonPress` is still called afterward if the guard resolves `true`.
   * Existing columns without this field are unaffected.
   */
  backButtonGuard?: () => Promise<boolean>;
  /** When true, the column content won't be wrapped in a ScrollView (useful for components with internal scroll) */
  disableScroll?: boolean;
}

// Context for passing click handlers and navigation to column content
export interface SlideLayoutContextType {
  /** Which column this component is rendered in */
  column: 'left' | 'right' | 'third' | 'fourth';
  /**
   * Current view mode (SINGLE or DUAL)
   */
  currentViewMode: ViewMode;
  /**
   * Execute a slide action and await for animation to complete
   * @param action - The slide action to execute (SINGLE, DUAL, NEXT, PREV, or default for fade in)
   * @returns Promise that resolves when the animation completes
   */
  onSlideChange: (action: SlideAction) => Promise<void>;/**
   * Set a custom title for a column by its id. Pass undefined to reset to default.
   * WARNING: Call this method only after the column has been rendered to avoid title reset on re-render.
   * @param title - New title for the column
   */
  setColumnTitle: (title: string | undefined) => void;
  /**
   * Set a full-column overlay for this column (e.g. unsaved changes sheet).
   * Pass null to clear.
   */
  setColumnOverlay: (overlay: ReactNode | null) => void;
  /**
   * Toggle the third (hidden) panel. When shown, the left panel fades out,
   * the right panel shifts left to become the new left, and the third panel
   * fades in as the new right. Calling again reverses the transition.
   */
  toggleThirdPanel: () => void;
}

export type SlidePanelSlot = 'right' | 'third' | 'fourth';

/**
 * Baked into the outlet context when SlideLayout renders its own `<Outlet />`
 * (see `enableChildRoutes`). Child route elements call `useSlidePanel(...)`,
 * which reads this registrar from `useOutletContext()` and registers the
 * column for its slot. Apps extend this type with their own fields via the
 * `outletContext` prop.
 */
export interface SlidePanelRegistrar {
  setSlidePanel: (slot: SlidePanelSlot, column: ColumnData | null) => void;
}

export interface SlideLayoutProps {
  leftColumn: ColumnData;
  /**
   * When `enableChildRoutes` is set, a column contributed by a child route
   * (via `useSlidePanel`) takes precedence; this prop is the fallback.
   */
  rightColumn?: ColumnData;
  thirdColumn?: ColumnData;
  /**
   * Optional fourth column that slides in from the right when the third column
   * is already open. Used for sub-editors opened from the third column (e.g. the
   * group editor opened from the asset edit form).
   *
   * API constraint: consumers must set `showBackButton: true` and
   * `onBackButtonPress` — there is no imperative `toggleFourthPanel` fallback.
   */
  fourthColumn?: ColumnData;
  /**
   * When omitted, derived as DUAL if any right column (prop or outlet-contributed)
   * is present, SINGLE otherwise.
   */
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  /** When this key changes while in DUAL mode, triggers a slide-in animation for the left column */
  transitionKey?: string;
  /**
   * When true, SlideLayout renders a React Router `<Outlet />` and exposes a
   * `SlidePanelRegistrar` on the outlet context, so child route elements can
   * register their columns via `useSlidePanel(...)` without the consuming
   * shell having to thread setters through `useOutletContext`.
   */
  enableChildRoutes?: boolean;
  /**
   * Additional fields to merge into the outlet context alongside the
   * `SlidePanelRegistrar`. Only used when `enableChildRoutes` is true.
   */
  outletContext?: object;
}